import './dashboard.css'
import React from 'react'
import { ErrorBoundary } from '../../components/common/ErrorBoundary'
import { Sidebar } from './components/Sidebar'
import { TopNav } from './components/TopNav'
import { DashboardOverviewPage } from './pages/DashboardOverviewPage'
import { ContactsPage } from './pages/ContactsPage'
import { LapsedPage } from './pages/LapsedPage'
import { EngagingPage } from './pages/EngagingPage'
import { RecoveredPage } from './pages/RecoveredPage'
import { MessagesPage } from './pages/MessagesPage'
import { ReportsPage } from './pages/ReportsPage'
import { SettingsPage } from './pages/SettingsPage'
import { BusinessPage } from './pages/BusinessPage'
import { ServicesPage } from './pages/ServicesPage'
import { ProfilePage } from './pages/ProfilePage'
import { AdminPage } from './pages/AdminPage'
import type { AdminTab } from './pages/AdminPage'
import { DashboardProvider, useDashboard } from './DashboardContext'
import { computeNudgeMetrics } from './nudgeMetrics'
import { ProfileSetupBanner } from './components/ProfileSetupBanner'
import { TrialBanner } from './components/TrialBanner'
import { TrialImportPrompt } from './components/TrialImportPrompt'
import { useMessagesNotification } from './hooks/useMessagesNotification'
import { markMessagesSeen } from '../../lib/notificationStorage'
import { loadAuth, clearAuth } from '../../lib/authStorage'
import { useNavigate } from 'react-router-dom'
import type { DashboardPageName } from './types'

// Shows at most one banner, in priority order:
//   1. Urgent trial (≤3 days left)
//   2. Blocking profile setup (missing critical fields)
//   3. Import prompt (trialing, no contacts)
//   4. Normal trial banner
//   5. Profile enrichment banner
function PriorityBannerSlot() {
  const { currentSubscription, contacts, businessInfo } = useDashboard()

  const sub = currentSubscription?.subscription
  const isTrialing = sub?.status === 'TRIALING'
  const trialDaysLeft = isTrialing && sub?.trialEnd
    ? Math.max(0, Math.ceil((new Date(sub.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null

  const isUrgentTrial = trialDaysLeft !== null && trialDaysLeft <= 3

  const hasBlockingProfileGap = businessInfo
    ? !businessInfo.businessType?.trim() || businessInfo.lapseWindowDays == null
    : false

  const hasNoContacts = contacts.length === 0

  if (isUrgentTrial) return <TrialBanner />
  if (hasBlockingProfileGap) return <ProfileSetupBanner />
  if (isTrialing && hasNoContacts) return <TrialImportPrompt />
  if (isTrialing) return <TrialBanner />
  return <ProfileSetupBanner />
}

function DashboardInner() {
  const { me, theme, setTheme, contacts, businessInfo, queueRows, activePage, setActivePage, currentSubscription, subscriptionLoading } = useDashboard()
  const businessName = businessInfo?.businessName ?? null
  const navigate = useNavigate()

  const lapseDays = businessInfo?.lapseWindowDays ?? 60
  const totalContacts = contacts.length
  const lapsedContacts = contacts.filter(c => {
    const lastVisit = new Date(c.lastVisitDate)
    const diffDays = Number.isNaN(lastVisit.getTime())
      ? 0
      : Math.floor((Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24))
    return diffDays >= lapseDays
  }).length

  const recoveredCount = computeNudgeMetrics(queueRows).totalRecoveredOnly

  // Engaging count: distinct contacts with at least one non-terminal row that has a reply.
  const engagingCount = React.useMemo(() => {
    const terminal = new Set(['RECOVERED','RETURNED','NO_SHOW','OPT_OUT','EXHAUSTED','FAILED'])
    const seen = new Set<string>()
    for (const row of queueRows) {
      if (terminal.has(row.status)) continue
      if (!row.replyReceivedAt) continue
      seen.add(row.contactId)
    }
    return seen.size
  }, [queueRows])

  const messagesNotifCount = useMessagesNotification()
  const isAdmin = loadAuth()?.role === 'ADMIN'

  function handleSelectPage(page: DashboardPageName) {
    if (page === 'messages') markMessagesSeen()
    setActivePage(page)
  }

  function handleSignOut() {
    clearAuth()
    navigate('/signin', { replace: true })
  }

  return (
    <div className="dash-root">
      <Sidebar
        activePage={activePage}
        onSelectPage={handleSelectPage}
        onSignOut={handleSignOut}
        user={me ?? undefined}
        businessName={businessName}
        businessLogoUrl={businessInfo?.logoUrl ?? null}
        contactsCount={totalContacts}
        lapsedCount={lapsedContacts}
        engagingCount={engagingCount}
        recoveredCount={recoveredCount}
        messagesNotifCount={messagesNotifCount}
        isAdmin={isAdmin}
        planDisplayName={subscriptionLoading ? null : (currentSubscription?.plan.displayName ?? 'Free')}
        subscriptionStatus={currentSubscription?.subscription?.status ?? null}
        cancelAtPeriodEnd={currentSubscription?.subscription?.cancelAtPeriodEnd ?? false}
      />

      <main className="main">
        <TopNav
          theme={theme}
          setTheme={setTheme}
          activePage={activePage}
          totalContacts={totalContacts}
          lapsedContacts={lapsedContacts}
          userFirstName={me?.firstName ?? null}
        />

        {activePage === 'dashboard' && <PriorityBannerSlot />}
        <DashboardOverviewPage isActive={activePage === 'dashboard'} />
        <ContactsPage isActive={activePage === 'contacts'} />
        <LapsedPage isActive={activePage === 'lapsed'} />
        <EngagingPage isActive={activePage === 'engaging'} />
        <RecoveredPage isActive={activePage === 'recovered'} />
        <MessagesPage isActive={activePage === 'messages'} />
        <ReportsPage isActive={activePage === 'reports'} />
        <BusinessPage isActive={activePage === 'business'} />
        <ServicesPage isActive={activePage === 'services'} />
        <SettingsPage isActive={activePage === 'settings'} />
        <ProfilePage isActive={activePage === 'profile'} />
        {isAdmin && (() => {
          const adminTab: AdminTab =
            activePage === 'admin-overview'    ? 'overview'
            : activePage === 'admin-metrics'   ? 'metrics'
            : activePage === 'admin-slots'     ? 'slots'
            : activePage === 'admin-businesses'? 'businesses'
            : activePage === 'admin-ai-health' ? 'ai-health'
            : activePage === 'admin-users'     ? 'users'
            : 'billing'
          return (
            <AdminPage
              isActive={
                activePage === 'admin-overview' ||
                activePage === 'admin-metrics' ||
                activePage === 'admin-slots'   ||
                activePage === 'admin-billing' ||
                activePage === 'admin-businesses' ||
                activePage === 'admin-ai-health' ||
                activePage === 'admin-users'
              }
              activeTab={adminTab}
            />
          )
        })()}
      </main>
    </div>
  )
}

export default function Dashboard() {
  return (
    <ErrorBoundary>
      <DashboardProvider>
        <DashboardInner />
      </DashboardProvider>
    </ErrorBoundary>
  )
}
